import { CommonUtils } from './../../shared/utils/common-utils';
import { Homekeepers } from './../../providers/homekeepers/homekeepers';
import { Aws } from './../../shared/providers/aws';
import { Camera } from './../../shared/providers/camera/camera';
import { Component, OnDestroy, ViewEncapsulation } from '@angular/core';

import { AppReview } from 'src/providers/app-review/app-review';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { AppState } from 'src/shared/providers/http/app-state';
import { OnRails } from 'src/providers/on-rails/on-rails';
import { SelfieVerificationService } from 'src/providers/selfie-verification/selfie-verification.service';

import { SuccessPageParamsModel } from 'src/pages/success/success';
import { CheckLocationPermission } from 'src/behaviors/check-location-permission';
import { TimeoutErrorHandler } from 'src/shared/providers/http/timeout-error-handler';
import { Timeout } from 'src/shared/components/timeout/timeout';
import { ActivatedRoute, Router } from '@angular/router';
import { IdentificationCheck } from 'src/shared/providers/identification-check';
import { Subscription, timer } from 'rxjs'
import { tap } from 'rxjs/operators';
import { selfieOptions } from 'src/shared/constants/camera';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { DateTime } from 'luxon';
import { FormatDateTimePipe } from 'src/shared/pipes/format-date-time.pipe';
import { Me } from 'src/shared/providers/me';
import { Concierge } from 'src/providers/concierge/concierge';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';
import { Capacitor } from '@capacitor/core';
import { HtmlVideoRecorder } from 'src/shared/providers/camera/html-video-recorder';
import * as moment from 'moment';

const IDENTIFICATION_CHECK_STATUSES = {
  0: 'requires_input',
  1: 'processing',
  2: 'verified',
}

@Component({
  templateUrl: 'check-in.html',
  styleUrls: ['./check-in.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CheckInPage extends Timeout implements OnDestroy {

  todayCleanings: any;
  errorMessage: any;
  isOnIdentificationCheck: boolean;
  isOnFaceUnlock: boolean;
  progressBars = [
    {
      mode: 'determinate',
      value: 100,
    },
    {
      mode: 'determinate',
      value: 0,
    },
    {
      mode: 'determinate',
      value: 0,
    },
  ];
  identificationSteps: Array<{icon: string, title: string, subtitle: string, displayButton: boolean, buttonText: string, action: () => void}> = [
    {
      icon: 'assets/icon/id.png',
      title: 'These jobs require a quick identification check.',
      subtitle: 'All you need is your drivers license.',
      displayButton: true,
      buttonText: 'Get Verified',
      action: () => this.getVerified(),
    },
    {
      icon: 'assets/icon/identify.png',
      title: 'Identification in progress',
      subtitle: 'This typically takes less than 1 minute.',
      displayButton: false,
      buttonText: '',
      action: () => {},
    },
    {
      icon: 'assets/icon/verify.png',
      title: 'Record a Quick Video',
      subtitle: '',
      displayButton: true,
      buttonText: 'Start Recording',
      action: () => this.startVideoRecording(),
    },
    {
      icon: 'assets/icon/verify.png',
      title: 'Verification Completed!',
      subtitle: 'Your identity has been succesfully verified.',
      displayButton: true,
      buttonText: 'Go to Check In',
      action: () => this.goToCheckIn(),
    }
  ];
  currentIdentificationStep = this.identificationSteps[0];
  verificationUrl = '';
  verificationSubscription = new Subscription();
  identificationError: any;
  selfieAttempts = 0;
  isVideoStep = false;
  videoDeadline: moment.Moment;
  lastIdentificationCheck: any;

  constructor(
    private appReview: AppReview,
    private checkLocationPermission: CheckLocationPermission,
    private customNavCtrl: CustomNavController,
    private onRails: OnRails,
    private appState: AppState,
    private identificationCheck: IdentificationCheck,
    private camera: Camera,
    private storage: TidyStorage,
    private aws: Aws,
    private homekeeper: Homekeepers,
    private me: Me,
    private utils: CommonUtils,
    private concierge: Concierge,
    private selfieVerification: SelfieVerificationService,
    private htmlVideoRecorder: HtmlVideoRecorder,
    timeoutErrorHandler: TimeoutErrorHandler,
    router: Router,
    route: ActivatedRoute
  ) {
    super(timeoutErrorHandler, router, route);
  }

  async ionViewDidEnter() {
    this.loaded = false;
    this.alreadyShowedOfflineOrTimeoutAlert = false;

    if (!await this.appState.isOnline()) {
      this.alreadyShowedOfflineOrTimeoutAlert = true;
      return this.timeoutErrorHandler.showOfflineAlert(this.successPageLoad, 'dashboard', this.retrySubject);
    }
    this.todayCleanings = await this.onRails.getTodayCleaningsToCheckin()
      .catch(err => this.timeoutHandler(err));
    if (this.todayCleanings.length === 0) {
      this.customNavCtrl.navigateForward('jobs');
      return;
    }
    await this.handleIdentificationCheck();

    this.successPageLoad.emit(true);
    this.loaded = true;
  }

  ngOnDestroy() {
    this.verificationSubscription?.unsubscribe();
  }

  async handleIdentificationCheck(): Promise<void> {
    this.resetProgressBar();
    const identificationCheck = await this.getIdentificationCheck();
    this.isOnIdentificationCheck = identificationCheck?.status !== IDENTIFICATION_CHECK_STATUSES[2];
    if (this.isOnIdentificationCheck) {
      this.currentIdentificationStep = (this.identificationError || identificationCheck?.status === IDENTIFICATION_CHECK_STATUSES[1]) ? this.identificationSteps[1] : this.identificationSteps[0];
      if (this.currentIdentificationStep === this.identificationSteps[1]) {
        this.progressBars[1] = {
          mode: 'indeterminate',
          value: 0,
        }
        this.startVerificationCheck();
      }
      return;
    }

    // ID is verified - check if video is still needed (only for new verifications)
    if (identificationCheck?.video_required && !identificationCheck?.video_url) {
      this.isOnIdentificationCheck = true;
      this.finishVerificationCheck();
      return;
    }

    this.handleFaceUnlock();
  }

  handleFaceUnlock(): void {
    const someJobTypeTidy = this.todayCleanings.some((cleaning) => cleaning?.job?.type?.toLowerCase()?.includes('tidy'));
    const someServiceTypeNotPrivate = this.todayCleanings.some((cleaning) => !cleaning?.service_type_details?.is_private);

    const hasAtLeastOneTidyJob = someJobTypeTidy || someServiceTypeNotPrivate;

    if (!hasAtLeastOneTidyJob) {
      this.goToCheckIn();
      return;
    }
    this.isOnFaceUnlock = true;
    this.identificationSteps = [
      {
        icon: 'assets/icon/face-auth.png',
        title: 'Account Security Verification',
        subtitle: 'We need to verify you with a selfie before continuing.',
        displayButton: true,
        buttonText: 'Take Selfie',
        action: () => this.takeSelfie(),
      },
      {
        icon: 'assets/icon/identify.png',
        title: 'Verifying your identity',
        subtitle: 'This typically takes less than 1 minute.',
        displayButton: false,
        buttonText: '',
        action: () => {},
      },
      {
        icon: 'assets/icon/verify.png',
        title: 'Verification Complete',
        subtitle: 'Your account is now secure.',
        displayButton: true,
        buttonText: 'Continue',
        action: () => this.goToCheckIn(),
      }
    ];
    this.currentIdentificationStep = this.identificationSteps[0];
  }

  async takeSelfie(): Promise<void> {
    this.currentIdentificationStep = this.identificationSteps[1];
    this.progressBars[1] = {
      mode: 'indeterminate',
      value: 0,
    }
    let me = await this.storage.retrieve('me');
    if (!me) {
      me = await this.me.load();
      this.storage.save('me', me);
    }


    this.camera.getPhoto(selfieOptions).then(async (cameraPhoto) => {
      try {
        if (!cameraPhoto?.base64String && !cameraPhoto?.path && !cameraPhoto?.webPath) {
          this.handleErrorOnSelfie();
          return;
        }

        this.selfieAttempts++;

        // Try to verify the selfie, but don't block upload on failure
        let verificationResultValue: 'approved' | 'rejected' | undefined;
        try {
          const verificationResult = await this.selfieVerification.verifySelfieAuto(cameraPhoto);
          verificationResultValue = verificationResult.isValid ? 'approved' : 'rejected';

          if (!verificationResult.isValid) {
            this.utils.showError(verificationResult.reason, 5000, false);
          }
        } catch (verificationError) {
          // AI service failed - log it and continue with upload
          console.error('AI verification service error, proceeding with upload:', verificationError);
        }

        // Always upload regardless of verification result
        const date = new FormatDateTimePipe().transform(DateTime.local().toISO(), 'LLL d');
        const objKey = `homekeepers/${me.user.id}/face-scans/${date}`;
        const s3Object = await this.aws.uploadImageToS3(cameraPhoto.base64String, objKey);
        const payload: any = {
          photo_url: s3Object.Location,
          type: 'photo',
          desc: `${new TranslationPipe().transform('Face scan for pro')} ${me.user.id} ${new TranslationPipe().transform('at')} ${date}`
        };
        if (verificationResultValue) {
          payload.result = verificationResultValue;
        }
        await this.homekeeper.createFaceScan(payload);
        this.finishVerificationCheck()
      } catch (error) {
        console.error(error);
        this.handleErrorOnSelfie();
      }
    }).catch(() => {
      this.handleErrorOnSelfie();
    });
  }

  handleErrorOnSelfie(): void {
    this.resetProgressBar();
    this.currentIdentificationStep = this.identificationSteps[0];
  }

  handleSelfieVerificationFailed(reason?: string): void {
    this.resetProgressBar();
    this.currentIdentificationStep = this.identificationSteps[0];
    const message = reason || 'Selfie verification failed. Please try again.';
    this.utils.showError(message, 5000, false);
  }

  resetProgressBar(): void {
    this.progressBars[1] = {
      mode: 'determinate',
      value: 0,
    }
    this.progressBars[2] = {
      mode: 'determinate',
      value: 0,
    }
  }

  goToVerificationPage(): void {
    window.open(this.verificationUrl, '_blank');
  }

  getVerified(): void {
    this.startVerificationCheck();
    this.goToVerificationPage();
    this.currentIdentificationStep = this.identificationSteps[1];
    this.progressBars[1] = {
      mode: 'indeterminate',
      value: 0,
    }
  }

  async startVerificationCheck(): Promise<void> {
    const fifteenSeconds = 15000;
    this.verificationSubscription.add(timer(0, fifteenSeconds)
      .pipe(
        tap(async () => {
          const identificationCheck = await this.getIdentificationCheck()
          if (identificationCheck.status === IDENTIFICATION_CHECK_STATUSES[2]) {
            this.finishVerificationCheck();
            this.verificationSubscription.unsubscribe();
          }
        })
      )
      .subscribe());
  }

  finishVerificationCheck(): void {
    // If in face unlock flow, just show the final step (no video needed)
    if (this.isOnFaceUnlock) {
      this.progressBars[1] = {
        mode: 'determinate',
        value: 100,
      };
      this.progressBars[2] = {
        mode: 'determinate',
        value: 100,
      };
      this.currentIdentificationStep = this.identificationSteps[2];
      return;
    }

    const check = this.lastIdentificationCheck;

    // If video not required (legacy user) or already uploaded, show success
    if (!check?.video_required || check?.video_url) {
      this.showFinalStep();
      return;
    }

    // Video required but not uploaded - show video step
    this.progressBars[1] = {
      mode: 'determinate',
      value: 100,
    };
    this.currentIdentificationStep = this.identificationSteps[2];
    this.isVideoStep = true;

    // Set deadline (show max 5 min even if more time, default to 5 min if not provided)
    const fiveMinFromNow = moment().add(5, 'minutes');
    if (check?.video_upload_deadline) {
      const deadline = moment(check.video_upload_deadline);
      this.videoDeadline = deadline.isBefore(fiveMinFromNow) ? deadline : fiveMinFromNow;
    } else {
      this.videoDeadline = fiveMinFromNow;
    }
  }

  async startVideoRecording(): Promise<void> {
    this.htmlVideoRecorder.openVideoRecorderModal().then(async (videoBlob) => {
      if (videoBlob) {
        this.uploadVideo(videoBlob).then(() => {});
      }
    }).catch((error) => {
      this.utils.showError('Video recording failed. Please try again.', 5000, false);
    });
  }

  async uploadVideo(videoBlob: Blob): Promise<void> {
    // Hide video instructions during upload
    this.isVideoStep = false;

    // Show uploading state
    this.currentIdentificationStep = {
      icon: 'assets/icon/identify.png',
      title: 'Uploading video...',
      subtitle: 'Please wait.',
      displayButton: false,
      buttonText: '',
      action: () => {},
    };
    this.progressBars[2] = {
      mode: 'indeterminate',
      value: 0,
    };

    try {
      const s3Response = await this.identificationCheck.uploadVideo(videoBlob);
      await this.identificationCheck.submitVideoUrl(s3Response.Location);
      this.showFinalStep();
    } catch (error) {
      this.utils.showError('Video upload failed. Please try again.', 5000, false);
      // Let user retry - show video step again with error
      this.currentIdentificationStep = this.identificationSteps[2];
      this.isVideoStep = true;
      this.progressBars[2] = {
        mode: 'determinate',
        value: 0,
      };
      const responseError = error?.error?.message || error?.message;
      const windowExpired = 'window has expired';
      if (responseError && responseError.includes(windowExpired)) {
        this.identificationError = { reason: `Video upload window has expired. Please restart the identification check.`}
        this.currentIdentificationStep = this.identificationSteps[0];
        return;
      }
      this.identificationError = { reason: `Video upload failed. Please try again. ${responseError}`}
    }
  }

  showFinalStep(): void {
    this.isVideoStep = false;
    this.identificationError = null;
    this.progressBars[1] = {
      mode: 'determinate',
      value: 100,
    };
    this.progressBars[2] = {
      mode: 'determinate',
      value: 100,
    };
    // In face unlock flow, steps array only has 3 elements (0-2), so use index 2 as final step
    // In normal identification flow, steps array has 4 elements (0-3), so use index 3 as final step
    const finalStepIndex = this.isOnFaceUnlock ? 2 : 3;
    this.currentIdentificationStep = this.identificationSteps[finalStepIndex];
  }

  async getIdentificationCheck(): Promise<any> {
    const identificationCheck = await this.identificationCheck.getIdentityCheck();
    this.lastIdentificationCheck = identificationCheck;
    this.verificationUrl = identificationCheck?.redirect_url;
    this.identificationError = identificationCheck?.last_error_message;
    console.log(identificationCheck);
    return identificationCheck;
  }

  goToCheckIn(): void {
    this.isOnIdentificationCheck = false;
    this.isOnFaceUnlock = false;
  }

  async confirm() {
    const locationCheck = await this.checkLocationPermission.requestLocationPermission();

    try {
      await this.onRails.confirmCleanings(this.extractJobIds());
      const params = this.successParams(locationCheck.success);
      this.customNavCtrl.navigateForward('success', params);
    } catch (err) {
      if (!await this.appState.isOnline()) {
        this.errorMessage = 'You Must Be Online To Check In';
      } else {
        this.errorMessage = err.message || err.error.message;
      }
    }
  }

  extractJobIds(): number[] {
    return this.todayCleanings.map( item => item.job.id );
  }

  callOut = () => {
    this.customNavCtrl.navigateForward('check-in-learn-about-teams', {cleanings: this.todayCleanings});
  }

  successParams(successLocation: boolean): SuccessPageParamsModel {
    return {
      header: 'Checked in for Work',
      body: 'Remember: Clients usually book based on your retention score.\
      The best thing you can do to get more Clients is to keep existing Clients.<br><br>\
      Clients tell us the most important reasons they will book with you again are:<br><br>\
      1) Keep your appointments<br>\
      2) Work hard the full job time<br>\
      3) Maintain a positive Attitude',
      buttonText: 'Ok',
      buttonRoute: successLocation ? 'dashboard' : 'location-warning'
    };
  }

  async reportFaceUnlockIssues(): Promise<void> {
    if (this.selfieAttempts < 1) {
      this.utils.showError('Please attempt the selfie verification first.', 5000, false);
      return;
    }
    const loading = await this.utils.showLoading();
    const payload = {
      type: 'support.homekeeper_other',
    	data: {
        subject: 'Account Security Verification Issues',
    		body: 'I am having trouble completing the account security verification.',
        emails: [],
        attachments: [],
    	}
    };
    try {
      await this.concierge.addConciergeItem(payload);
      loading.dismiss();
      this.utils.showSuccess('Your issue has been reported. We will get back to you shortly.');
      this.goToCheckIn();
    } catch (err) {
      console.error(err);
      loading.dismiss();
      this.utils.showError('There was an error reporting your issue. Please try again later.', 5000);
    }
  }
}
